/**
 * Analyzer: ATM Version Trend (Phase 17)
 *
 * Sibling of atmVersionComparison. Where that analyzer does pairwise
 * "current vs prior" comparison, this one looks at the ARC across 3+
 * versions and surfaces multi-step patterns the trader is iterating
 * through.
 *
 * Four patterns:
 *
 *   1. MONOTONIC IMPROVEMENT — every step in the chain has higher WR
 *      than the prior. Strong positive signal: the trader is on a
 *      productive iteration arc. Suggests CONTINUING in the same
 *      direction (e.g., "you've widened Trail Freq 12→16→24, each
 *      step +WR; try 32 next").
 *
 *   2. MONOTONIC REGRESSION — every step has lower WR. The trader is
 *      walking the edge downward without realizing it. Stronger than
 *      pairwise WARNING because the regression has been sustained.
 *
 *   3. U-SHAPE — improved then regressed (or vice-versa). The sweet
 *      spot was a middle version. Suggests rolling forward TO the apex
 *      instead of blanket-rolling-back.
 *
 *   4. NOISE — no consistent direction. Coach stays quiet (returns null)
 *      so we don't bury the trader in interpretation.
 *
 * Why a separate analyzer instead of extending atmVersionComparison:
 *   - Different mental model: "what's the arc?" not "did the latest
 *     change help?"
 *   - Different observation severity ladder: monotonic-regression is
 *     a 90-priority alert; sweet-spot is a 75-priority insight
 *   - Different action language: "try X next" / "roll forward to N-2"
 *     vs "roll back / lock in"
 *
 * Reads:
 *   trade.atmStrategy, trade.netPnl, trade.date, trade.entryTime, trade.rMultiple
 *   ntTemplateVersions[atmName][] — version objects from useAtmManagement
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { findVersionForTrade, diffAtmVersions, diffFieldLabel } from '../../../ntTemplateParser.js';

export const key = 'atmVersionTrend';
export const tier = 'free';

// Need 3+ versions to call a "trend" — 2 is just a comparison and is
// already covered by atmVersionComparison.
const MIN_VERSIONS_FOR_TREND = 3;
// Need this many trades per version. Slightly lower than the pairwise
// analyzer's 4 because we're looking at MULTIPLE versions and want to
// surface the arc even when later versions are still fresh.
const MIN_TRADES_PER_VERSION = 3;
// Total WR spread across the arc — if the best-vs-worst gap is below
// this, the "trend" is just noise and we stay quiet.
const MIN_TOTAL_SPREAD = 0.20;

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';

const computeStats = (trades) => {
  const wins = trades.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length;
  const wr = trades.length > 0 ? wins / trades.length : 0;
  const rs = trades.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
  const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : null;
  return { count: trades.length, wins, wr, avgR };
};

/**
 * Detect the arc pattern over a chronologically-ordered WR sequence.
 * Returns one of: 'improving', 'regressing', 'peak', 'valley', 'noise'.
 *
 * Strictly monotonic check (every step in the same direction) — keeps
 * the signal honest. A "mostly improving" arc with one regression in
 * the middle falls into the U-shape / noise bucket on purpose.
 */
const detectPattern = (wrSeries) => {
  if (!Array.isArray(wrSeries) || wrSeries.length < MIN_VERSIONS_FOR_TREND) return 'noise';
  const n = wrSeries.length;

  // Strictly monotonic checks — every step must move in the same direction.
  // Allow tiny equal-WR plateaus (within 1pp) so floating-point noise on
  // 4-trade samples doesn't break the pattern.
  const EPS = 0.01;
  let allUp = true, allDown = true;
  for (let i = 1; i < n; i++) {
    const delta = wrSeries[i] - wrSeries[i - 1];
    if (delta < -EPS) allUp = false;
    if (delta > EPS) allDown = false;
  }
  if (allUp) return 'improving';
  if (allDown) return 'regressing';

  // Single-apex check — find the max index; if all to the left are
  // non-decreasing and all to the right are non-increasing, it's a peak
  // (sweet spot in the middle of the arc).
  const maxIdx = wrSeries.indexOf(Math.max(...wrSeries));
  if (maxIdx > 0 && maxIdx < n - 1) {
    let leftRising = true, rightFalling = true;
    for (let i = 1; i <= maxIdx; i++) if (wrSeries[i] - wrSeries[i - 1] < -EPS) leftRising = false;
    for (let i = maxIdx + 1; i < n; i++) if (wrSeries[i] - wrSeries[i - 1] > EPS) rightFalling = false;
    if (leftRising && rightFalling) return 'peak';
  }

  // Single-trough check — same shape, inverted. Less actionable but
  // worth surfacing as "the middle config was a mistake."
  const minIdx = wrSeries.indexOf(Math.min(...wrSeries));
  if (minIdx > 0 && minIdx < n - 1) {
    let leftFalling = true, rightRising = true;
    for (let i = 1; i <= minIdx; i++) if (wrSeries[i] - wrSeries[i - 1] > EPS) leftFalling = false;
    for (let i = minIdx + 1; i < n; i++) if (wrSeries[i] - wrSeries[i - 1] < -EPS) rightRising = false;
    if (leftFalling && rightRising) return 'valley';
  }

  return 'noise';
};

/**
 * Extract numeric-param trend from chronological version diffs. Given
 * diffs between consecutive versions, find params that moved in the
 * SAME direction (always wider, always tighter) across the arc. This
 * is what powers the "try X next" suggestion in monotonic improvement.
 *
 * Returns: array of { field, bracket?, step?, direction, sequence: [v1, v2, ...] }
 */
const extractDirectionalParams = (versionsChrono) => {
  if (!Array.isArray(versionsChrono) || versionsChrono.length < 2) return [];

  // Map key → values across versions: `${field}|${bracket}|${step}` → [v1, v2, ...]
  const seriesByKey = new Map();
  for (let i = 1; i < versionsChrono.length; i++) {
    const diffs = diffAtmVersions(versionsChrono[i - 1], versionsChrono[i]);
    for (const d of diffs) {
      if (typeof d.old !== 'number' || typeof d.new !== 'number') continue;
      const key = `${d.field}|${d.bracket ?? ''}|${d.step ?? ''}`;
      if (!seriesByKey.has(key)) seriesByKey.set(key, { field: d.field, bracket: d.bracket, step: d.step, sequence: [d.old] });
      seriesByKey.get(key).sequence.push(d.new);
    }
  }

  const out = [];
  for (const entry of seriesByKey.values()) {
    if (entry.sequence.length < 3) continue;  // need 3+ points for a trend
    let allUp = true, allDown = true;
    for (let i = 1; i < entry.sequence.length; i++) {
      const delta = entry.sequence[i] - entry.sequence[i - 1];
      if (delta <= 0) allUp = false;
      if (delta >= 0) allDown = false;
    }
    if (allUp) out.push({ ...entry, direction: 'increasing' });
    else if (allDown) out.push({ ...entry, direction: 'decreasing' });
  }
  return out;
};

/**
 * Suggest the next value in a strictly-monotonic numeric sequence.
 * Uses the average step size — a simple extrapolation that matches how
 * traders typically iterate (consistent step deltas, not exponential).
 */
const suggestNext = (sequence) => {
  if (!Array.isArray(sequence) || sequence.length < 2) return null;
  let totalStep = 0;
  for (let i = 1; i < sequence.length; i++) totalStep += sequence[i] - sequence[i - 1];
  const avgStep = totalStep / (sequence.length - 1);
  const last = sequence[sequence.length - 1];
  return Math.round(last + avgStep);
};

export const analyze = (ctx) => {
  const { atmName, atmTrades, ntTemplateVersions } = ctx || {};
  if (!atmName || !Array.isArray(atmTrades) || atmTrades.length === 0) return null;
  if (!ntTemplateVersions || typeof ntTemplateVersions !== 'object') return null;

  const versions = ntTemplateVersions[atmName];
  if (!Array.isArray(versions) || versions.length < MIN_VERSIONS_FOR_TREND) return null;

  // Attribute each trade to its active version
  const byVersion = new Map();
  for (const t of atmTrades) {
    const v = findVersionForTrade(t, versions);
    if (!v?.versionId) continue;
    if (!byVersion.has(v.versionId)) byVersion.set(v.versionId, { version: v, trades: [] });
    byVersion.get(v.versionId).trades.push(t);
  }

  // Filter to versions with enough samples + walk in chronological (oldest-first)
  // order. The version array is newest-first; reverse to get chronological.
  const versionsChrono = [...versions].reverse();
  const arc = [];
  for (const v of versionsChrono) {
    const bucket = byVersion.get(v.versionId);
    if (!bucket || bucket.trades.length < MIN_TRADES_PER_VERSION) continue;
    arc.push({ version: v, ...computeStats(bucket.trades) });
  }
  if (arc.length < MIN_VERSIONS_FOR_TREND) return null;

  const wrSeries = arc.map(a => a.wr);
  const spread = Math.max(...wrSeries) - Math.min(...wrSeries);
  if (spread < MIN_TOTAL_SPREAD) return null;

  const pattern = detectPattern(wrSeries);
  if (pattern === 'noise') return null;

  // Build a compact scoreboard for the detail line: v1 25% → v2 38% → v3 52%
  const scoreboard = arc.map(a => fmtPct(a.wr)).join(' → ');
  const versionsArcStr = arc.map(a => a.version.label?.trim() || a.version.versionId.slice(-6)).join(' → ');

  if (pattern === 'improving') {
    // Find what's moving in the right direction so we can suggest a next step
    const directional = extractDirectionalParams(arc.map(a => a.version));
    const suggestion = directional.length > 0
      ? directional.slice(0, 2).map(d => {
          const next = suggestNext(d.sequence);
          const label = diffFieldLabel(d.field);
          const where = [d.bracket != null ? `B${d.bracket + 1}` : '', d.step != null ? `s${d.step + 1}` : ''].filter(Boolean).join(' ');
          return `${where ? where + ' ' : ''}${label} ${d.sequence.join('→')}, try ${next}`;
        }).join('; ')
      : '';
    return makeObservation({
      id: `${key}:${atmName}:improving`,
      analyzer: key,
      scope: 'atm',
      scopeId: atmName,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.SETUP,
      title: `"${atmName}" — monotonic improvement across ${arc.length} versions (${fmtPct(spread)} arc)`,
      detail: `WR: ${scoreboard}. Each iteration has improved this template. ${suggestion ? `Pattern: ${suggestion}.` : ''}`,
      action: suggestion
        ? `Keep iterating in the same direction — ${suggestion.split(';')[0].trim()}.`
        : `Whatever you've been changing on each iteration is working. Note what differs between the versions and continue in that direction.`,
      evidence: { arc: arc.map(a => ({ versionId: a.version.versionId, wr: a.wr, count: a.count })), spread, pattern, directional },
      confidence: 0.78,
      priority: 80,
    });
  }

  if (pattern === 'regressing') {
    return makeObservation({
      id: `${key}:${atmName}:regressing`,
      analyzer: key,
      scope: 'atm',
      scopeId: atmName,
      severity: SEVERITY.WARNING,
      category: CATEGORY.SETUP,
      title: `"${atmName}" — monotonic regression across ${arc.length} versions (lost ${fmtPct(spread)} of WR)`,
      detail: `WR: ${scoreboard}. Every iteration on this template has bled more edge than the prior. The current version is the worst in the arc.`,
      action: `Roll back to the earliest version in this arc (${arc[0].version.label?.trim() || 'baseline'}) and reconsider the iteration premise — whatever direction you've been pushing, it's the wrong one.`,
      evidence: { arc: arc.map(a => ({ versionId: a.version.versionId, wr: a.wr, count: a.count })), spread, pattern },
      confidence: 0.82,
      priority: 90,
    });
  }

  if (pattern === 'peak') {
    const apexIdx = wrSeries.indexOf(Math.max(...wrSeries));
    const apex = arc[apexIdx];
    return makeObservation({
      id: `${key}:${atmName}:peak`,
      analyzer: key,
      scope: 'atm',
      scopeId: atmName,
      severity: SEVERITY.WARNING,
      category: CATEGORY.SETUP,
      title: `"${atmName}" — you overshot the sweet spot (peak was ${fmtPct(apex.wr)}, now ${fmtPct(arc[arc.length - 1].wr)})`,
      detail: `WR arc: ${scoreboard}. The middle version (${apex.version.label?.trim() || 'v' + (apexIdx + 1)}) was the high-water mark — subsequent changes walked the edge back down.`,
      action: `Roll forward to the apex config rather than back to baseline — the apex's bracket structure had the right balance for this template.`,
      evidence: { arc: arc.map(a => ({ versionId: a.version.versionId, wr: a.wr, count: a.count })), spread, pattern, apexIdx, apexVersionId: apex.version.versionId },
      confidence: 0.75,
      priority: 82,
    });
  }

  if (pattern === 'valley') {
    return makeObservation({
      id: `${key}:${atmName}:valley`,
      analyzer: key,
      scope: 'atm',
      scopeId: atmName,
      severity: SEVERITY.INFO,
      category: CATEGORY.SETUP,
      title: `"${atmName}" — recovered from a mid-arc dip (now back to ${fmtPct(arc[arc.length - 1].wr)})`,
      detail: `WR arc: ${scoreboard}. The middle config was a mistake — current is back near baseline. Worth noting what differed about the bad middle config so you don't repeat it.`,
      action: `Document what differed about the middle version so future iterations skip the same hole.`,
      evidence: { arc: arc.map(a => ({ versionId: a.version.versionId, wr: a.wr, count: a.count })), spread, pattern },
      confidence: 0.65,
      priority: 60,
    });
  }

  return null;
};

// Test-friendly exports
export {
  computeStats,
  detectPattern,
  extractDirectionalParams,
  suggestNext,
  MIN_VERSIONS_FOR_TREND,
  MIN_TRADES_PER_VERSION,
  MIN_TOTAL_SPREAD,
};
